<template>
  <div
    ref="notificationElement"
    :class="['notification', { 'is-read': notification.read, 'is-fading': fading, 'is-mobile': isMobile }]"
    @mouseenter="handleHover"
    @click="handleClick"
  >
    <div class="meta">
      <div v-if="isWelcomeNotification" class="picture app">
        <img :src="traceMarkerSrc" class="trace-marker-icon" />
      </div>

      <div v-else-if="isBoardNotification" class="picture app vector">
        <span class="board-squares-icon" />
      </div>

      <div v-else-if="isAppNotification" class="picture app vector">
        <GamIcon
          :name="getAppIcon"
          :size="GamIconSize.X_LARGE"
          :is-primary="isAppIconPrimary"
          :is-error="isAppIconError"
        />
      </div>

      <img v-else-if="typeof notification.profileImageBlobHash === 'string'" class="picture" :src="getImageSrc()" />

      <div v-else-if="notification.senderEntityName?.toLowerCase() === 'user'" class="picture vector">
        <GamIcon :name="GamIconName.PAWN_EMPTY" :size="GamIconSize.LARGE" />
      </div>

      <div v-else-if="notification.senderEntityName?.toLowerCase() === 'club'" class="picture vector">
        <GamIcon :name="GamIconName.MUTUAL_EMPTY" :size="GamIconSize.LARGE" />
      </div>
    </div>

    <div class="data">
      <div class="title">{{ notification.title }}</div>
      <div class="body">{{ notification.body }}</div>
      <div class="timestamp">{{ formatDate(new Date(notification.createdAt), 'MMM do yy・HH:mm') }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SenderEntity, type NotificationDto } from '@/core/data/notification/notification.interface';
import { NotificationPreferenceType } from '@/core/data/user/user.interface';
import { gambits } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { AppEndpoint } from '@/core/network/api/constants/api.url.constant';
import { useNotificationStore } from '@/stores/notification.store';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useIntersectionObserver } from '@vueuse/core';
import { formatDate } from 'date-fns';
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const READ_DELAY = 600;

interface Props {
  notification: NotificationDto;
}

const { notification } = defineProps<Props>();
const { id, path, read } = notification;

const notificationStore = useNotificationStore();
const router = useRouter();

const notificationElement = ref<HTMLDivElement>();
const isVisible = shallowRef<boolean>(false);
const readTimeout = shallowRef<number>();
const fading = shallowRef<boolean>(false);

const { stop } = useIntersectionObserver(notificationElement, ([entry]) => {
  isVisible.value = entry?.isIntersecting || false;
});

onBeforeUnmount(() => {
  stop();
});

function handleMarkAsRead() {
  notificationStore.markAsRead(id);
  fading.value = true;
}

function handleHover() {
  fading.value = false;
}

function handleClick() {
  notificationStore.markAsRead(id);
  const historySuffix = path.includes('?') ? '&' : '?';
  router.push(`${path}${historySuffix}back=history`);
}

// The app icon rather than the favicon: this sits on a chessboard swatch and
// needs an opaque square, where the favicon is a transparent pink disc.
const traceMarkerSrc = '/favicon/app-icon-v6.png';

const isAppNotification = computed(() => notification.senderEntityName === SenderEntity.APP);
const isWelcomeNotification = computed(
  () => isAppNotification.value && notification.type === NotificationPreferenceType.WELCOME,
);

const IRL_PROPOSED_TYPES = new Set([
  NotificationPreferenceType.SPARK_ACTIVITY,
  NotificationPreferenceType.SPARK_PROPOSAL_PLAYER_VOTED,
]);

const IRL_CONFIRMED_TYPES = new Set([
  NotificationPreferenceType.SPARK_BOOKED,
  NotificationPreferenceType.SPARK_CONFIRMED,
  NotificationPreferenceType.SPARK_MATCHED,
  NotificationPreferenceType.GAMBIT_NEARBY_RECOMMENDED_ELO,
  NotificationPreferenceType.GAMBIT_NEARBY_FOLLOWED_PLAYER,
  NotificationPreferenceType.GAMBIT_NEARBY_FOLLOWED_CLUB,
  NotificationPreferenceType.GAMBIT_NEARBY_FOLLOWED_LOCATION,
  NotificationPreferenceType.GAMBIT_ATTENDEE_CHANGED,
  NotificationPreferenceType.GAMBIT_ATTENDEE_JOINED,
  NotificationPreferenceType.GAMBIT_ATTENDEE_LEFT,
  NotificationPreferenceType.GAMBIT_ATTENDEE_ORGANIZER_LEFT,
  NotificationPreferenceType.GAMBIT_ORGANIZER_CHANGED,
  NotificationPreferenceType.GAMBIT_ORGANIZER_JOINED,
  NotificationPreferenceType.GAMBIT_ORGANIZER_LEFT,
]);

const IRL_SQUARE_TYPES = new Set([
  NotificationPreferenceType.SPARK_COMPROMISED,
  NotificationPreferenceType.GAMBIT_ATTENDEE_EVERYONE_LEFT,
  NotificationPreferenceType.GAMBIT_ATTENDEE_CANCELED,
  NotificationPreferenceType.GAMBIT_ORGANIZER_EVERYONE_LEFT,
  NotificationPreferenceType.GAMBIT_ORGANIZER_CANCELED,
  NotificationPreferenceType.GAMBIT_OWNER_EVERYONE_LEFT,
]);

const BOARD_TYPES = new Set([
  NotificationPreferenceType.BOARD_MOVE_ON_POSITION,
  NotificationPreferenceType.BOARD_MOVE_ON_PLANTED,
  NotificationPreferenceType.BOARD_COMPLETED,
]);

const isBoardNotification = computed(() => isAppNotification.value && BOARD_TYPES.has(notification.type));

const getAppIcon = computed(() => {
  if (IRL_SQUARE_TYPES.has(notification.type)) {
    return GamIconName.PLAY;
  }
  if (IRL_PROPOSED_TYPES.has(notification.type) || IRL_CONFIRMED_TYPES.has(notification.type)) {
    return GamIconName.PLAY_EMPTY;
  }
  return GamIconName.LOGO;
});

const isAppIconError = computed(() => IRL_SQUARE_TYPES.has(notification.type));

const isAppIconPrimary = computed(() => IRL_CONFIRMED_TYPES.has(notification.type));

const getImageSrc = (): string => {
  let url = `${gambits.configService.getServiceRoutingHost()}${AppEndpoint.BLOB}`;
  url = url.replace(new RegExp('{blobHash}', 'g'), notification.profileImageBlobHash || '');

  return `${url}?width=256`;
};

watch(isVisible, (isVisible) => {
  if (read) return;

  if (isVisible) {
    readTimeout.value = setTimeout(handleMarkAsRead, READ_DELAY) as any as number;
  } else {
    clearTimeout(readTimeout.value);
  }
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.notification {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: row;
  gap: var(--spacing-l);
  padding-top: var(--spacing-xl);
  padding-bottom: var(--spacing-xl);
  border-radius: var(--radius-small);
  background-color: transparent;
  border: 1px solid var(--color-white-10);
  cursor: pointer;
  transition:
    background-color var(--transition),
    border-color var(--transition);

  &.is-fading {
    transition:
      background-color 800ms,
      border-color 800ms !important;
  }

  @include hover {
    background-color: var(--color-white-5);
    border-color: var(--color-white-10);
    transition:
      background-color 32ms,
      border-color 32ms;
  }

  &:not(.is-read) {
    background-color: var(--color-dark-600);
    border-color: transparent;
  }
}

.notification.is-mobile {
  flex-direction: row;
  padding-top: calc(var(--spacing-md) + var(--spacing-s) + 4px);
  padding-bottom: calc(var(--spacing-md) + var(--spacing-s) + 4px);
}

.is-mobile .data {
  padding-right: var(--spacing-l);
  gap: calc(var(--spacing-md) + 2px);
  order: 2;
}

.meta {
  width: 6rem;
  flex-shrink: 0;
  padding-top: 1px;
  padding-left: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  align-items: center;
}

.is-mobile .meta {
  box-sizing: border-box;
  order: 1;
  width: calc(48px + var(--spacing-l));
  padding-left: var(--spacing-l);
}

.picture {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  object-fit: cover;
  overflow: hidden;
  border-radius: 48px !important;
  filter: saturate(0);

  &.vector {
    background: #222;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.app {
    filter: none;
  }
}

.board-squares-icon {
  width: 19px;
  height: 19px;
  border-radius: 3px;
  overflow: hidden;
  background-repeat: no-repeat;
  background-size: 50% 50%;
  background-position:
    0 0,
    100% 0,
    0 100%,
    100% 100%;
  background-image: linear-gradient(#f0d9b5, #f0d9b5), linear-gradient(#b58863, #b58863),
    linear-gradient(#b58863, #b58863), linear-gradient(#f0d9b5, #f0d9b5);
}

.trace-marker-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.timestamp {
  color: var(--color-white-30);
  font-size: 13px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 300;
}

.data {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  padding-right: var(--spacing-xxl);
}

.title {
  font-size: 14px;
  color: var(--color-white-30);
  font-weight: 500;
  letter-spacing: 0.033em;
}

.body {
  font-size: 18px;
  font-weight: 200;
  line-height: 130%;
  letter-spacing: 0.02em;
  text-wrap: balance;
  transform: translateY(-2px);
}

.is-mobile .body {
  font-size: 16px;
  transform: translateY(-1px);
}
</style>
